import { Activity, ShieldTick } from "@untitledui/icons";
import { useState } from "react";
import { useAssuranceOverviewV2 } from "@/api/assurance/assurance-overview";
import { useBlockedCreatorsCount } from "@/api/blocklist";
import { Skeleton } from "@/components/ui/skeleton";
import { useGmvFormatter } from "@/hooks/use-gmv-formatter";
import { getStartDateISO } from "@/utils/date-conversion";
import { formatViews, formatViewsCompact } from "@/utils/formatters";
import { CreamCard } from "./CreamCard";
import { SafetyEmptyState } from "./SafetyEmptyState";
import { SectionDropdownLabel } from "./SectionDropdownLabel";
import { StatTile } from "./StatTile";

export const PerformanceSection = () => {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useAssuranceOverviewV2({ startDate: getStartDateISO(days) });
  const { data: blockedCreators } = useBlockedCreatorsCount({
    startDate: getStartDateISO(days),
  });
  const formatGmv = useGmvFormatter();

  const isEmpty = data && data.totalVideos === 0;

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          <Activity className="size-5 text-secondary" />
          <p className="text-base text-secondary font-medium">Performance</p>
        </div>
        <SectionDropdownLabel days={days} onChange={setDays} />
      </div>
      {isLoading || !data ? (
        <Skeleton className="h-[200px] w-full rounded-xl" />
      ) : isEmpty ? (
        <SafetyEmptyState
          title="No performance data yet"
          subtitle="No videos have been analysed in the last 30 days."
          width="100%"
          height="200px"
        />
      ) : (
        <CreamCard className="flex gap-2">
          <StatTile
            value={formatViews(data.totalVideos)}
            label="Total Videos Analysed"
            layout="label-first"
            showInfoIcon
            tooltipText="All videos processed and analysed for risks in the last 30 days, regardless of whether a risk was found."
            subtext="System protected"
            subtextTone="success"
            subtextIcon={ShieldTick}
          />
          <StatTile
            value={`${(data.totalVideos > 0 ? (data.videosWithAddFlagAction / data.totalVideos) * 100 : 0).toFixed(1)}%`}
            label="Videos with risks confirmed"
            layout="label-first"
            showInfoIcon
            tooltipText="Videos with a confirmed risk in the last 30 days ÷ total videos analysed in the last 30 days."
            subtext={`${formatViews(data.videosWithAddFlagAction)} videos`}
            subtextTone="neutral"
          />
          <StatTile
            value={formatViewsCompact(data.riskyImpressionsBlocked)}
            label="Risky video views before removal"
            layout="label-first"
            showInfoIcon
            tooltipText="Total views these videos had already accrued before they were blocked or removed for a confirmed risk (last 30 days)."
            subtext="protected"
            subtextTone="success"
          />
          <StatTile
            value={formatGmv(data.gmvBlocked)}
            label="GMV accrued from blocked videos"
            layout="label-first"
            showInfoIcon
            tooltipText="GMV from videos before the shoppable links were removed or the video was deleted"
            subtext="at-risk revenue"
            subtextTone="success"
          />
          <StatTile
            value={formatViews(data.videosRemovedCount)}
            label="Videos removed"
            layout="label-first"
            showInfoIcon
            tooltipText="Total number of videos removed in the last 30 days."
            subtext="from ads"
            subtextTone="neutral"
          />
          {blockedCreators && (
            <StatTile
              value={formatViews(blockedCreators.total)}
              label="Creators blocked"
              layout="label-first"
              showInfoIcon
              tooltipText="Total number of creators blocked in the last 30 days."
              subtext="repeat offenders"
              subtextTone="neutral"
            />
          )}
        </CreamCard>
      )}
    </div>
  );
};
